import { createSlice } from '@reduxjs/toolkit';
import { dispatch } from '../store';
import { callApi } from '../../utils/apiCall';

const initialState = {
    permissions: {},
    roles: [],
    loading: false,
    error: null,
    // practiceId the current `permissions` map was fetched for. Tracked so
    // `hasFeatureAccess` can detect a stale-practice read during the
    // practice-switch window (old practice's permissions are still in the
    // slice while `fetchMyPermissions(newId)` is in flight) and refuse to
    // green-light feature-gated thunks for the *new* practice based on the
    // *old* practice's answers. See utils/featurePermission.js.
    practiceId: null,
    // practiceId the in-flight `fetchMyPermissions` is targeting (set when
    // loading flips to true). Lets callers compare their intended fetch
    // against the practice we're currently resolving permissions for.
    loadingPracticeId: null,
    // Super Admin state
    features: [],
    practiceFeatures: [],
    rolePermissions: [],
    userOverrides: [],
};

const slice = createSlice({
    name: 'permissions',
    initialState,
    reducers: {
        setPermissions(state, action) {
            state.permissions = action.payload.permissions || {};
            state.roles = action.payload.roles || [];
            // payload.practiceId is supplied by fetchMyPermissions — record
            // which practice this snapshot is for so feature-gate callers
            // can validate against the current target.
            if (action.payload.practiceId !== undefined) {
                state.practiceId = action.payload.practiceId;
            }
        },
        setLoading(state, action) {
            // Accept either a boolean (legacy) or {loading, practiceId}.
            if (typeof action.payload === 'object' && action.payload !== null) {
                state.loading = !!action.payload.loading;
                if (action.payload.loading) {
                    state.loadingPracticeId = action.payload.practiceId ?? null;
                } else {
                    state.loadingPracticeId = null;
                }
            } else {
                state.loading = !!action.payload;
                if (!action.payload) state.loadingPracticeId = null;
            }
        },
        setError(state, action) {
            state.error = action.payload;
        },
        setFeatures(state, action) {
            state.features = action.payload;
        },
        setPracticeFeatures(state, action) {
            state.practiceFeatures = action.payload;
        },
        setRolePermissions(state, action) {
            state.rolePermissions = action.payload;
        },
        setUserOverrides(state, action) {
            state.userOverrides = action.payload;
        },
        clearPermissions(state) {
            state.permissions = {};
            state.roles = [];
            state.practiceId = null;
            state.loadingPracticeId = null;
        },
    },
});

export default slice.reducer;
export const { setPermissions, clearPermissions } = slice.actions;

// --------------- Thunks ---------------

export function fetchMyPermissions(practiceId) {
    return async () => {
        try {
            // Tag the loading flag with the target practiceId so that
            // feature-gate callers in flight can compare against it and skip
            // stale-practice fires during the practice-switch window.
            dispatch(slice.actions.setLoading({ loading: true, practiceId }));
            const response = await callApi(`/api/v1/permissions/my-permissions/?practice_id=${practiceId}`);
            if (response.status === 200) {
                // Stamp the snapshot with practiceId so hasFeatureAccess can
                // detect cross-practice reads.
                dispatch(slice.actions.setPermissions({ ...response.data, practiceId }));
            }
        } catch (error) {
            dispatch(slice.actions.setError(error.message));
        } finally {
            dispatch(slice.actions.setLoading({ loading: false, practiceId }));
        }
    };
}

export function fetchFeatures() {
    return async () => {
        try {
            dispatch(slice.actions.setLoading(true));
            const response = await callApi('/api/v1/permissions/features/');
            if (response.status === 200) {
                dispatch(slice.actions.setFeatures(response.data?.results || response.data || []));
            }
        } catch (error) {
            dispatch(slice.actions.setError(error.message));
        } finally {
            dispatch(slice.actions.setLoading(false));
        }
    };
}

export function createFeature(data, callback) {
    return async () => {
        try {
            dispatch(slice.actions.setLoading(true));
            const response = await callApi('/api/v1/permissions/features/', 'POST', data);
            if (response.status === 201) {
                if (callback) callback(response.data);
            }
        } catch (error) {
            dispatch(slice.actions.setError(error.message));
        } finally {
            dispatch(slice.actions.setLoading(false));
        }
    };
}

export function updateFeature(id, data, callback) {
    return async () => {
        try {
            dispatch(slice.actions.setLoading(true));
            const response = await callApi(`/api/v1/permissions/features/${id}/`, 'PATCH', data);
            if (response.status === 200) {
                if (callback) callback(response.data);
            }
        } catch (error) {
            dispatch(slice.actions.setError(error.message));
        } finally {
            dispatch(slice.actions.setLoading(false));
        }
    };
}

export function fetchRolePermissions(featureId) {
    return async () => {
        try {
            dispatch(slice.actions.setLoading(true));
            const response = await callApi(`/api/v1/permissions/features/${featureId}/role-permissions/`);
            if (response.status === 200) {
                dispatch(slice.actions.setRolePermissions(response.data));
            }
        } catch (error) {
            dispatch(slice.actions.setError(error.message));
        } finally {
            dispatch(slice.actions.setLoading(false));
        }
    };
}

export function updateRolePermissions(featureId, permissions, callback) {
    return async () => {
        try {
            dispatch(slice.actions.setLoading(true));
            const response = await callApi(`/api/v1/permissions/features/${featureId}/role-permissions/`, 'PUT', permissions);
            if (response.status === 200) {
                if (callback) callback(response.data);
            }
        } catch (error) {
            dispatch(slice.actions.setError(error.message));
        } finally {
            dispatch(slice.actions.setLoading(false));
        }
    };
}

export function fetchPracticeFeatures(featureId) {
    return async () => {
        try {
            dispatch(slice.actions.setLoading(true));
            const response = await callApi(`/api/v1/permissions/practice-features/?feature__id=${featureId}`);
            if (response.status === 200) {
                dispatch(slice.actions.setPracticeFeatures(response.data?.results || response.data || []));
            }
        } catch (error) {
            dispatch(slice.actions.setError(error.message));
        } finally {
            dispatch(slice.actions.setLoading(false));
        }
    };
}

export function createPracticeFeature(data, callback) {
    return async () => {
        try {
            dispatch(slice.actions.setLoading(true));
            const response = await callApi('/api/v1/permissions/practice-features/', 'POST', data);
            if (response.status === 201) {
                if (callback) callback(response.data);
            }
        } catch (error) {
            dispatch(slice.actions.setError(error.message));
        } finally {
            dispatch(slice.actions.setLoading(false));
        }
    };
}

export function togglePracticeFeature(id, isEnabled, callback) {
    return async () => {
        try {
            dispatch(slice.actions.setLoading(true));
            const response = await callApi(`/api/v1/permissions/practice-features/${id}/`, 'PATCH', { is_enabled: isEnabled });
            if (response.status === 200) {
                if (callback) callback(response.data);
            }
        } catch (error) {
            dispatch(slice.actions.setError(error.message));
        } finally {
            dispatch(slice.actions.setLoading(false));
        }
    };
}

export function bulkTogglePracticeFeatures(data, callback) {
    return async () => {
        try {
            dispatch(slice.actions.setLoading(true));
            const response = await callApi('/api/v1/permissions/practice-features/bulk/', 'POST', data);
            if (response.status === 200) {
                if (callback) callback(response.data);
            }
        } catch (error) {
            dispatch(slice.actions.setError(error.message));
        } finally {
            dispatch(slice.actions.setLoading(false));
        }
    };
}

export function fetchUserOverrides(featureId) {
    return async () => {
        try {
            dispatch(slice.actions.setLoading(true));
            const response = await callApi(`/api/v1/permissions/user-overrides/?feature__id=${featureId}`);
            if (response.status === 200) {
                dispatch(slice.actions.setUserOverrides(response.data?.results || response.data || []));
            }
        } catch (error) {
            dispatch(slice.actions.setError(error.message));
        } finally {
            dispatch(slice.actions.setLoading(false));
        }
    };
}

export function createUserOverride(data, callback) {
    return async () => {
        try {
            dispatch(slice.actions.setLoading(true));
            const response = await callApi('/api/v1/permissions/user-overrides/', 'POST', data);
            if (response.status === 201) {
                if (callback) callback(response.data);
            }
        } catch (error) {
            dispatch(slice.actions.setError(error.message));
        } finally {
            dispatch(slice.actions.setLoading(false));
        }
    };
}

export function updateUserOverride(id, data, callback) {
    return async () => {
        try {
            dispatch(slice.actions.setLoading(true));
            const response = await callApi(`/api/v1/permissions/user-overrides/${id}/`, 'PATCH', data);
            if (response.status === 200) {
                if (callback) callback(response.data);
            }
        } catch (error) {
            dispatch(slice.actions.setError(error.message));
        } finally {
            dispatch(slice.actions.setLoading(false));
        }
    };
}

export function deleteUserOverride(id, callback) {
    return async () => {
        try {
            dispatch(slice.actions.setLoading(true));
            const response = await callApi(`/api/v1/permissions/user-overrides/${id}/`, 'DELETE');
            if (response.status === 204) {
                if (callback) callback();
            }
        } catch (error) {
            dispatch(slice.actions.setError(error.message));
        } finally {
            dispatch(slice.actions.setLoading(false));
        }
    };
}

export function addUserRole(practiceUserId, role, callback) {
    return async () => {
        try {
            dispatch(slice.actions.setLoading(true));
            const response = await callApi(`/api/v1/permissions/practice-users/${practiceUserId}/roles/`, 'POST', { role });
            if (response.status === 201) {
                if (callback) callback(response.data);
            }
        } catch (error) {
            dispatch(slice.actions.setError(error.message));
        } finally {
            dispatch(slice.actions.setLoading(false));
        }
    };
}

export function removeUserRole(practiceUserId, role, callback) {
    return async () => {
        try {
            dispatch(slice.actions.setLoading(true));
            const response = await callApi(`/api/v1/permissions/practice-users/${practiceUserId}/roles/${role}/`, 'DELETE');
            if (response.status === 204) {
                if (callback) callback();
            }
        } catch (error) {
            dispatch(slice.actions.setError(error.message));
        } finally {
            dispatch(slice.actions.setLoading(false));
        }
    };
}
